import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import Loader from "../../pages/Loader";
import Swal from "sweetalert2";
import {
  FiImage,
  FiCamera,
  FiUpload,
  FiSave,
  FiArrowRight,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiTrash2,
  FiVideo
} from "react-icons/fi";
import {
  FaImages,
  FaPhotoVideo,
  FaRegImage,
  FaUpload,
  FaPlayCircle
} from "react-icons/fa";

const AddGallery = () => {
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileType, setFileType] = useState('image');

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (fileType === 'image' && file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      if (fileType === 'video' && file.size > 100 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Video size should be less than 100MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      const isValid = fileType === 'image' ? file.type.startsWith('image/') : file.type.startsWith('video/');
      if (!isValid) {
        Swal.fire({
          title: "Invalid File",
          text: `Please select a ${fileType} file`,
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      if (fileType === 'image' && file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      if (fileType === 'video' && file.size > 100 * 1024 * 1024) {
        Swal.fire({
          title: "File Too Large",
          text: "Video size should be less than 100MB",
          icon: "warning",
          confirmButtonColor: "#6366f1"
        });
        return;
      }
      setImagePreview(URL.createObjectURL(file));
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      document.getElementById('gallery-image').files = dataTransfer.files;
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const fileInput = document.getElementById('gallery-image');
      if (!fileInput.files || !fileInput.files[0]) {
        throw new Error("Please select a file");
      }

      const formData = new FormData();
      formData.append("file", fileInput.files[0]);
      formData.append("upload_preset", "prayagproperties");

      // Use appropriate Cloudinary endpoint
      const cloudinaryUrl = fileType === 'image' 
        ? "https://api.cloudinary.com/v1_1/djmyigser/image/upload"
        : "https://api.cloudinary.com/v1_1/djmyigser/video/upload";

      const cloudinaryResponse = await fetch(cloudinaryUrl, {
        method: "POST",
        body: formData,
      });

      const cloudinaryData = await cloudinaryResponse.json();

      if (!cloudinaryData.secure_url) {
        throw new Error("Failed to upload to Cloudinary");
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/create_gallery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: data.title,
          photo: cloudinaryData.secure_url,
          type: fileType
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        Swal.fire({
          title: "Success!",
          text: `Gallery ${fileType} added successfully.`,
          icon: "success",
          confirmButtonColor: "#6366f1",
          timer: 2000,
          timerProgressBar: true,
          background: "#ffffff"
        });
        reset();
        setImagePreview(null);
        setFileType('image');
      } else {
        Swal.fire({
          title: "Error",
          text: result.message || "Something went wrong.",
          icon: "error",
          confirmButtonColor: "#ef4444"
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Something went wrong.",
        icon: "error",
        confirmButtonColor: "#ef4444"
      });
    } finally {
      setLoading(false);
    }
  };

  const watchedTitle = watch("title");
  const imageFile = watch("certificateImage");

  const inputClasses = "w-full px-4 py-3 rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10 flex items-center justify-center">
      <div className="container mx-auto px-4 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <FaImages className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-xl">Add Gallery Image</h2>
                <p className="text-indigo-100 text-sm mt-0.5">Add a new image to the gallery</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* File Type Selector */}
            <div>
              <label className={labelClasses}>
                File Type
              </label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setFileType('image');
                    setImagePreview(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                    fileType === 'image'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <FiImage className="w-5 h-5" />
                  Image
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFileType('video');
                    setImagePreview(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                    fileType === 'video'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <FiVideo className="w-5 h-5" />
                  Video
                </button>
              </div>
            </div>

            {/* Title Field */}
            <div>
              <label className={labelClasses}>
                {fileType === 'image' ? 'Image' : 'Video'} Title <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                {fileType === 'image' ? (
                  <FiImage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                ) : (
                  <FiVideo className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                )}
                <input
                  type="text"
                  {...register("title", { required: "Title is required." })}
                  className={`${inputClasses} pl-12 ${errors.title ? 'border-red-500' : ''}`}
                  placeholder={`Enter ${fileType} title`}
                />
              </div>
              {errors.title && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* File Upload Field */}
            <div>
              <label className={labelClasses}>
                Upload {fileType === 'image' ? 'Image' : 'Video'} <span className="text-red-500">*</span>
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                  isDragging 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : imagePreview 
                      ? 'border-emerald-300 bg-emerald-50/30'
                      : errors.certificateImage 
                        ? 'border-red-300 bg-red-50/30' 
                        : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30'
                }`}
                onClick={() => document.getElementById('gallery-image').click()}
              >
                <input
                  id="gallery-image"
                  type="file"
                  accept={fileType === 'image' ? 'image/*' : 'video/*'}
                  {...register("certificateImage", { required: true })}
                  onChange={handleFileChange}
                  className="hidden"
                />
                
                {imagePreview ? (
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      {fileType === 'image' ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-40 h-40 object-cover rounded-xl shadow-lg border-4 border-white"
                        />
                      ) : (
                        <video
                          src={imagePreview}
                          className="w-64 h-40 object-cover rounded-xl shadow-lg border-4 border-white"
                          controls
                        />
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImagePreview(null);
                          document.getElementById('gallery-image').value = '';
                        }}
                        className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-sm text-emerald-600 mt-3 flex items-center gap-1">
                      <FiCheckCircle className="w-4 h-4" />
                      {fileType === 'image' ? 'Image' : 'Video'} uploaded successfully
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-3">
                      {fileType === 'image' ? (
                        <FiCamera className="w-6 h-6 text-indigo-500" />
                      ) : (
                        <FiVideo className="w-6 h-6 text-indigo-500" />
                      )}
                    </div>
                    <p className="text-gray-600 font-medium">Click or drag to upload</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {fileType === 'image' ? 'PNG, JPG, GIF up to 5MB' : 'MP4, MOV up to 100MB'}
                    </p>
                  </div>
                )}
              </div>
              {errors.certificateImage && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <FiAlertCircle className="w-3 h-3" />
                  {fileType === 'image' ? 'Image' : 'Video'} is required
                </p>
              )}
            </div>

            {/* Preview Section */}
            {watchedTitle && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100"
              >
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FiCheckCircle className="w-4 h-4 text-emerald-600" />
                  Preview
                </h4>
                <div className="flex items-center gap-4">
                  {imagePreview ? (
                    fileType === 'image' ? (
                      <img src={imagePreview} alt="Preview" className="w-12 h-12 rounded-lg object-cover border-2 border-indigo-200" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                        <FaPlayCircle className="w-6 h-6 text-indigo-500" />
                      </div>
                    )
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                      {fileType === 'image' ? (
                        <FaRegImage className="w-6 h-6 text-indigo-500" />
                      ) : (
                        <FiVideo className="w-6 h-6 text-indigo-500" />
                      )}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-800">{watchedTitle}</p>
                    <p className="text-xs text-gray-400">
                      Gallery {fileType === 'image' ? 'Image' : 'Video'}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Adding to Gallery...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Add to Gallery
                    <FiArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            {/* Security Note */}
            <div className="text-center pt-2">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <FiShield className="w-3 h-3" />
                {fileType === 'image' ? 'Image' : 'Video'} is securely stored in the cloud
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddGallery;